import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { ArrowLeft } from "lucide-react";

export default function RegisterPage() {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", mobile: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await register(form);
      if (result && result.pending) {
        toast.success(result.message || "Registration submitted for review.", { duration: 6000 });
        navigate("/login");
      } else {
        toast.success("Account created.");
        navigate("/portal");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="text-sm text-slate-500 mb-6 inline-flex items-center gap-2" data-testid="register-back-home"><ArrowLeft className="h-4 w-4"/> Home</Link>
        <div className="eyebrow">Create account</div>
        <h2 className="font-serif text-4xl mt-3">Join VCAJ portal.</h2>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
          <div>
            <Label className="eyebrow">Full name</Label>
            <Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-2 rounded-sm" data-testid="register-name"/>
          </div>
          <div>
            <Label className="eyebrow">Email</Label>
            <Input type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="mt-2 rounded-sm" data-testid="register-email"/>
          </div>
          <div>
            <Label className="eyebrow">Mobile</Label>
            <Input value={form.mobile} onChange={(e)=>setForm({...form, mobile: e.target.value})} className="mt-2 rounded-sm" data-testid="register-mobile"/>
          </div>
          <div>
            <Label className="eyebrow">Password</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} className="mt-2 rounded-sm" data-testid="register-password"/>
          </div>
          <div>
            <Label className="eyebrow">Role</Label>
            <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
              <SelectTrigger className="mt-2 rounded-sm" data-testid="register-role"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">Employee / Article</SelectItem>
                <SelectItem value="manager">Project Manager</SelectItem>
                <SelectItem value="accounts">Accounts Team</SelectItem>
                <SelectItem value="admin">Admin / HR</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm h-11" data-testid="register-submit">{busy ? "Creating..." : "Create account"}</Button>
        </form>

        <p className="text-sm text-slate-600 mt-8 text-center">
          Already have an account? <Link to="/login" className="text-slate-900 underline underline-offset-4" data-testid="register-to-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
